import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { sowService } from '../../services/sowService';
import { SOW } from '../../models/sow';
import { 
  ArrowLeft, 
  FileText, 
  User, 
  MapPin, 
  Building, 
  Home, 
  Bed, 
  Bath, 
  DollarSign,
  Clock,
  CheckCircle,
  PlayCircle,
  AlertCircle,
  Loader2,
  Trash2,
  ChevronRight
} from 'lucide-react';
import { useAuthToken } from '../../components/AuthTokenProvider';
import { useAuth0 } from '@auth0/auth0-react';
import { sowConversationService } from '../../services/sowConversationService';
import { ImageGallery } from '../../components/ImageGallery';
import { ProjectEstimate } from '../../components/ProjectEstimate';
import { EstimateBreakdown } from '../../components/SOW/EstimateBreakdown';
import { ProjectHero } from '../../components/SOW/ProjectHero';
import { EvolvingEstimate } from '../../components/SOW/EvolvingEstimate';

interface SOWOverviewPageProps {}

export const SOWOverviewPage: React.FC<SOWOverviewPageProps> = () => {
  const { sowId } = useParams<{ sowId: string }>();
  const navigate = useNavigate();
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();
  const { getAccessTokenSilently } = useAuth0();

  // Detect if we're in public mode (route starts with /scope instead of /sow)
  const isPublicMode = window.location.pathname.startsWith('/scope/');

  const [sow, setSOW] = useState<SOW | null>(null);
  const [questionnaires, setQuestionnaires] = useState<any[]>([]);
  const [filteredQuestionCounts, setFilteredQuestionCounts] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllItems, setShowAllItems] = useState(false);
  const [preventAutoRedirect, setPreventAutoRedirect] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [projectEstimate, setProjectEstimate] = useState<any>(null);
  const [quickEstimate, setQuickEstimate] = useState<any>(null);
  const [generatingQuickEstimate, setGeneratingQuickEstimate] = useState(false);
  const [generatingProjectEstimate, setGeneratingProjectEstimate] = useState(false);
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const quickEstimateGeneratedRef = useRef<string | null>(null);
  const [sowImages, setSOWImages] = useState<any[]>([]);

  // Calculate questionnaire stats for EvolvingEstimate component
  const questionnaireStats = React.useMemo(() => {
    if (!questionnaires || questionnaires.length === 0) {
      return { pending: 0, inProgress: 0, completed: 0 };
    }
    return {
      pending: questionnaires.filter((q: any) => q.status === 'pending').length,
      inProgress: questionnaires.filter((q: any) => q.status === 'in_progress').length,
      completed: questionnaires.filter((q: any) => q.status === 'completed').length,
    };
  }, [questionnaires]);

  useEffect(() => {
    if (sowId && (isPublicMode || (tokenSet && !tokenLoading))) {
      // Check if user is coming back from a questionnaire page
      const fromQuestionnaire = document.referrer.includes('/questionnaire/');
      if (fromQuestionnaire) {
        setPreventAutoRedirect(true);
      }
      loadSOW(sowId);
    }
  }, [sowId, tokenSet, tokenLoading, isPublicMode]);

  // Generate quick estimate when questionnaire is complete
  useEffect(() => {
    if (!sowId || !sow || !sow.questionnaireCompleted) return;

    // Skip if SOW already has a quick estimate from backend
    if (sow.quickEstimate) {
      console.log('Quick estimate already exists, skipping generation');
      return;
    }

    // Check if we've already generated an estimate for this SOW in this session
    if (quickEstimateGeneratedRef.current === sowId) return;

    // Mark as generated to prevent multiple calls
    quickEstimateGeneratedRef.current = sowId;
    setGeneratingQuickEstimate(true);

    sowService.generateQuickEstimate(sowId, !isPublicMode)
      .then((estimate) => {
        console.log('Quick estimate generated:', estimate);
        setQuickEstimate(estimate);
        setGeneratingQuickEstimate(false);
      })
      .catch((error) => {
        console.error('Error generating quick estimate:', error);
        setGeneratingQuickEstimate(false);
        // Reset ref on error so user can retry
        quickEstimateGeneratedRef.current = null;
      });
  }, [sowId, sow?.questionnaireCompleted, sow?.quickEstimate, isPublicMode]);

  const loadSOW = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const sowData = await sowService.getSOW(id, !isPublicMode);
      setSOW(sowData);

      // Load existing quick estimate if available
      if (sowData.quickEstimate) {
        setQuickEstimate(sowData.quickEstimate);
      }

      // Check if SOW is incomplete and should redirect to wizard (only for authenticated users)
      if (!isPublicMode) {
        const isIncomplete = !sowData.address || !sowData.propertyData || !sowData.scopeDefinition;
        const isDraftStatus = sowData.status === 'draft' || sowData.status === 'scope-gathering' || !sowData.status;

        if (isIncomplete || isDraftStatus) {
          // SOW is not ready for overview, redirect to wizard
          navigate(`/sow/${id}/wizard`);
          return;
        }
      }

      // Check for existing estimate if SOW has projectPlanId or is estimate_ready
      if (sowData.projectPlanId || sowData.status === 'estimate_ready') {
        try {
          console.log('Loading existing project estimate...');
          const estimateResult = await sowService.generateProjectEstimate(id);
          setProjectEstimate(estimateResult);
        } catch (error) {
          console.error('Failed to load existing estimate:', error);
          // Continue loading the page even if estimate loading fails
        }
      }

      // Load questionnaires if SOW is in estimating status (NEW ARCHITECTURE)
      if (sowData.status === 'estimating') {
        try {
          const questionnairesData = await sowConversationService.getQuestionnaires(id);
          setQuestionnaires(questionnairesData.questionnaires);
          // Update work items with latest data from questionnaire evaluation
          setSOW(prev => prev ? { ...prev, workItems: questionnairesData.workItems } : null);
        } catch (qError) {
          console.log('No questionnaires found, using legacy work item refinement');
          setQuestionnaires([]);
        }
      }

      // Load images for the SOW
      try {
        const imagesResponse = await sowService.getSOWImages(id, !isPublicMode);
        setSOWImages(imagesResponse.images || []);
      } catch (imgError) {
        console.log('No images found for this SOW');
        setSOWImages([]);
      }
    } catch (err) {
      console.error('Error loading SOW:', err);
      setError('Failed to load SOW. It may not exist or you may not have access.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'complete':
        return { 
          icon: CheckCircle, 
          variant: 'default' as const, 
          color: 'text-green-600',
          description: 'Project completed - all work items finished'
        };
      case 'estimating':
        return { 
          icon: Clock, 
          variant: 'secondary' as const, 
          color: 'text-blue-600',
          description: 'Managing work items and tracking progress'
        };
      case 'initialQuestionnaire':
        return { 
          icon: FileText, 
          variant: 'outline' as const, 
          color: 'text-blue-600',
          description: 'Ready for questionnaire to generate work items'
        };
      case 'scope-gathering':
        return { 
          icon: PlayCircle, 
          variant: 'outline' as const, 
          color: 'text-orange-600',
          description: 'Setting up project scope and details'
        };
      default:
        return { 
          icon: AlertCircle, 
          variant: 'outline' as const, 
          color: 'text-gray-600',
          description: 'Project status unknown'
        };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper to convert paths for public mode
  const getRoutePath = (path: string) => {
    if (isPublicMode) {
      return path.replace('/sow/', '/scope/');
    }
    return path;
  };

  const handleStartQuestionnaire = () => {
    navigate(getRoutePath(`/sow/${sowId}/questionnaire`));
  };

  const handleContinue = () => {
    if (sow?.status === 'initialQuestionnaire' && !sow.questionnaireCompleted) {
      navigate(getRoutePath(`/sow/${sowId}/questionnaire`));
    } else if (sow?.status === 'draft' || sow?.status === 'scope-gathering') {
      if (!isPublicMode) {
        navigate(`/sow/${sowId}/wizard`);
      }
    } else if (sow?.status === 'estimating') {
      // NEW ARCHITECTURE: Navigate to first pending questionnaire or stay on overview
      const pendingQuestionnaires = questionnaires.filter((q: any) => q.status === 'pending');
      if (pendingQuestionnaires.length > 0) {
        navigate(getRoutePath(`/sow/${sowId}/questionnaire/${pendingQuestionnaires[0].id}`));
      } else {
        // All questionnaires complete, scroll to work items section
        const workItemsSection = document.querySelector('[data-section="work-items"]');
        if (workItemsSection) {
          workItemsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleViewResults = () => {
    setShowAllItems(!showAllItems);
  };

  const handleStartRefinement = async (workItemId: string) => {
    if (!sowId) return;
    
    try {
      // Start refinement conversation
      const response = await sowConversationService.startWorkItemRefinement(sowId, workItemId);
      
      // Navigate to refinement conversation (we'll create this route)
      navigate(`/sow/${sowId}/workitem/${workItemId}/refinement/${response.conversation.id}`);
    } catch (error) {
      console.error('Error starting work item refinement:', error);
    }
  };

  const handleContinueRefinement = (conversationId: string, workItemId: string) => {
    navigate(`/sow/${sowId}/workitem/${workItemId}/refinement/${conversationId}`);
  };

  // For EvolvingEstimate component - navigate to first pending/in-progress questionnaire
  const handleContinueRefinementFromEstimate = () => {
    if (!sowId) return;

    // Find first pending or in-progress questionnaire
    const pendingQuestionnaire = questionnaires.find((q: any) => q.status === 'pending');
    const inProgressQuestionnaire = questionnaires.find((q: any) => q.status === 'in_progress');
    const nextQuestionnaire = pendingQuestionnaire || inProgressQuestionnaire;

    if (nextQuestionnaire) {
      const routePath = isPublicMode ? `/scope/${sowId}/questionnaire/${nextQuestionnaire.id}` : `/sow/${sowId}/questionnaire/${nextQuestionnaire.id}`;
      navigate(routePath);
    }
  };

  const handleRemoveWorkItem = async (workItemId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!window.confirm('Are you sure you want to remove this work item?')) {
      return;
    }

    try {
      // TODO: Implement remove work item API call
      // await sowService.removeWorkItem(sowId, workItemId);
      
      // For now, just reload the SOW data
      if (sowId) {
        loadSOW(sowId);
      }
    } catch (error) {
      console.error('Error removing work item:', error);
    }
  };

  const handleGenerateEstimate = async () => {
    if (!sowId) return;

    try {
      setGeneratingProjectEstimate(true);
      setEstimateLoading(true);
      setError(null);

      const estimateResult = await sowService.generateProjectEstimate(sowId);
      setProjectEstimate(estimateResult);

      // Reload SOW to get updated totalEstimate
      await loadSOW(sowId);

    } catch (error) {
      console.error('Error generating estimate:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate estimate');
    } finally {
      setGeneratingProjectEstimate(false);
      setEstimateLoading(false);
    }
  };

  const handleViewEstimateDetails = () => {
    // TODO: Navigate to detailed estimate view
    console.log('View estimate details:', projectEstimate);
  };

  const handleDownloadEstimate = async () => {
    if (!projectEstimate || !sow || !sowId) {
      console.error('Missing project estimate, SOW data, or SOW ID for PDF generation');
      return;
    }

    try {
      console.log('📄 Downloading PDF estimate from backend...');
      
      // Get the access token
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: 'https://fxer.ai/api',
          scope: 'read:all',
        },
      });
      
      // Call the backend PDF generation endpoint  
      const response = await fetch(`http://localhost:3000/statementofwork/${sowId}/estimate/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Get the PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      const fileName = contentDisposition?.match(/filename="([^"]+)"/)?.[1] || 'Project_Estimate.pdf';
      a.download = fileName;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('✅ PDF downloaded successfully');
      
    } catch (error) {
      console.error('❌ Failed to download PDF:', error);
      // Could add a toast notification here
    }
  };

  // Sort work items: pending first, in_progress second, completed (auto-refined) last
  const sortWorkItems = (items: any[]) => {
    return items
      .filter(item => item.status !== 'REMOVED')
      .sort((a, b) => {
        // Items without refinementStatus (inspection items) go to the end
        if (!a.refinementStatus && !b.refinementStatus) return 0;
        if (!a.refinementStatus) return 1;
        if (!b.refinementStatus) return -1;
        
        const statusOrder: Record<string, number> = { 'pending': 1, 'in_progress': 2, 'completed': 3 };
        return (statusOrder[a.refinementStatus] || 999) - (statusOrder[b.refinementStatus] || 999);
      });
  };

  // Get work item counts for different statuses
  const getWorkItemCounts = () => {
    const questionnaire = sow?.workItems.filter(item => item.refinementStatus) || [];
    const inspection = sow?.workItems.filter(item => !item.refinementStatus && item.status !== 'REMOVED') || [];
    
    return {
      needsDetails: questionnaire.filter(item => item.refinementStatus === 'pending').length,
      needsRefinement: inspection.length, // Inspection items that need manual refinement
      inProgress: questionnaire.filter(item => item.refinementStatus === 'in_progress').length,
      completed: questionnaire.filter(item => item.refinementStatus === 'completed').length,
      total: questionnaire.length + inspection.length
    };
  };


  if (loading || (!isPublicMode && (tokenLoading || !tokenSet))) {
    return (
      <div className={`${isPublicMode ? 'min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100' : 'container mx-auto'} p-6`}>
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <div className="text-lg">Loading SOW...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !sow) {
    return (
      <div className="container mx-auto p-6">
        <Card className="text-center py-12">
          <CardContent className="pt-6">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">SOW Not Found</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/sow')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to SOWs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(sow.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="container mx-auto p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/sow')}
          className="mb-3 md:mb-4 text-sm md:text-base p-2 md:p-3"
        >
          <ArrowLeft className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
          Back to SOWs
        </Button>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 md:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Project</h1>
            <div className="flex items-start gap-2 text-gray-600">
              <MapPin className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0 mt-1" />
              <span className="text-sm md:text-base break-words">{sow.address}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <Badge variant={statusInfo.variant} className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm">
              <StatusIcon className={`h-3 w-3 md:h-4 md:w-4 ${statusInfo.color}`} />
              {sow.status}
            </Badge>
          </div>
        </div>
      </div>

      {/* Project Status - Top Level */}
      <div className="mb-4 md:mb-6">
        <Card>
          <CardContent className="p-3 md:p-4">
            {/* Refinement Progress - Show during estimating phase */}
            {sow.status === 'estimating' && questionnaires.length > 0 ? (
              <div className="grid md:grid-cols-[auto,1fr,auto] gap-6 md:gap-8 items-center">
                {/* Current Estimate */}
                <div className="flex flex-col">
                  <div className="text-xs text-gray-500 mb-1">Current Estimate</div>
                  <div className="text-xl md:text-2xl font-bold text-gray-900">
                    {sow.quickEstimate?.totalCostLow && sow.quickEstimate?.totalCostHigh ? (
                      `$${sow.quickEstimate.totalCostLow.toLocaleString()} - $${sow.quickEstimate.totalCostHigh.toLocaleString()}`
                    ) : generatingQuickEstimate ? (
                      <span className="flex items-center gap-2 text-blue-600">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Generating...
                      </span>
                    ) : (
                      <span className="text-gray-400">Pending</span>
                    )}
                  </div>
                </div>

                {/* Progress Section */}
                <div className="space-y-2 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium text-gray-700">
                      Refinement Progress
                    </span>
                    <span className="text-xs text-gray-600 whitespace-nowrap">
                      {questionnaireStats.completed} of {questionnaires.length} complete
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-blue-600 h-full transition-all duration-500 ease-out"
                      style={{
                        width: `${questionnaires.length > 0 ? (questionnaireStats.completed / questionnaires.length) * 100 : 0}%`
                      }}
                    />
                  </div>
                </div>

                {/* Continue Button */}
                <Button onClick={handleContinue} size="sm" className="text-sm md:text-base px-6 py-5 flex-shrink-0">
                  Continue
                </Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-2 md:gap-3 flex-1 min-w-0">
                  <StatusIcon className={`h-4 w-4 md:h-5 md:w-5 ${statusInfo.color} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm md:text-base">Project Status: {sow.status}</div>
                    <div className="text-xs md:text-sm text-gray-600">{statusInfo.description}</div>
                  </div>
                </div>
                {((sow.status === 'initialQuestionnaire' && !sow.questionnaireCompleted) ||
                  sow.status === 'draft' ||
                  sow.status === 'scope-gathering') && (
                  <Button onClick={handleContinue} size="sm" className="w-full sm:w-auto text-sm md:text-base px-4 md:px-6">
                    Continue
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PHASE 1: New Single-Column Layout */}
      <div className="space-y-4 md:space-y-6">
        {/* Hero Section - Images + Context */}
        <ProjectHero
          sow={sow}
          images={sowImages}
          onViewAllImages={() => setImageGalleryOpen(true)}
        />

        {/* Evolving Estimate - Adapts to Status */}
        <EvolvingEstimate
          sow={sow}
          onStartQuestionnaire={handleStartQuestionnaire}
          onRefineEstimate={handleContinueRefinementFromEstimate}
          onGenerateEstimate={handleGenerateEstimate}
          onViewBreakdown={() => {
            // Scroll to work items breakdown
            const element = document.querySelector('[data-section="work-items"]');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          generatingQuickEstimate={generatingQuickEstimate}
          generatingProjectEstimate={generatingProjectEstimate}
          questionnairesLoaded={questionnaires.length > 0}
          questionnaireStats={questionnaireStats}
        />

        {/* OLD Quick Estimate Display - DEPRECATED, keeping temporarily for reference */}
        {false && (quickEstimate || generatingQuickEstimate) && (
            <Card>
              <CardHeader className="p-3 md:p-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <DollarSign className="h-4 w-4 md:h-5 md:w-5" />
                  Quick Estimate
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-6 pt-0">
                {generatingQuickEstimate ? (
                  <div className="text-center py-6 md:py-8">
                    <Loader2 className="h-8 w-8 md:h-10 md:w-10 animate-spin mx-auto mb-3 md:mb-4 text-blue-600" />
                    <h3 className="text-base md:text-lg font-semibold mb-2">Generating Your Estimate...</h3>
                    <p className="text-xs md:text-sm text-gray-600">
                      We're analyzing your questionnaire responses and images to create an accurate estimate.
                    </p>
                  </div>
                ) : quickEstimate ? (
                  <div className="space-y-4 md:space-y-6">
                    {/* Estimate Range */}
                    <div className="text-center p-4 md:p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                      <div className="text-xs md:text-sm text-gray-600 mb-2">Estimated Project Cost</div>
                      <div className="text-2xl md:text-4xl font-bold text-blue-900">
                        ${quickEstimate.totalCostLow?.toLocaleString() || '0'} - ${quickEstimate.totalCostHigh?.toLocaleString() || '0'}
                      </div>
                      <div className="mt-2 md:mt-3 inline-flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 bg-white/80 rounded-full text-xs md:text-sm">
                        <CheckCircle className="h-3 w-3 md:h-4 md:w-4 text-green-600" />
                        <span className="font-medium text-green-900">Quick Estimate</span>
                      </div>
                    </div>

                    {/* Cost Breakdown */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-900">Cost Breakdown</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="text-xs text-gray-600 mb-1">Labor</div>
                          <div className="text-sm font-semibold text-gray-900">
                            ${quickEstimate.totalLaborLow?.toLocaleString() || '0'} - ${quickEstimate.totalLaborHigh?.toLocaleString() || '0'}
                          </div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="text-xs text-gray-600 mb-1">Materials</div>
                          <div className="text-sm font-semibold text-gray-900">
                            ${quickEstimate.totalMaterialLow?.toLocaleString() || '0'} - ${quickEstimate.totalMaterialHigh?.toLocaleString() || '0'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Category Breakdown */}
                    {quickEstimate.breakdown && quickEstimate.breakdown.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-900">By Category</h4>
                        <div className="space-y-2">
                          {quickEstimate.breakdown.map((item: {
                            category: string;
                            laborCostLow: number;
                            laborCostHigh: number;
                            materialCostLow: number;
                            materialCostHigh: number;
                            totalLow: number;
                            totalHigh: number;
                          }, index: number) => (
                            <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-900">{item.category}</span>
                                <span className="text-sm font-semibold text-gray-900">
                                  ${item.totalLow?.toLocaleString()} - ${item.totalHigh?.toLocaleString()}
                                </span>
                              </div>
                              <div className="flex gap-4 text-xs text-gray-600">
                                <span>Labor: ${item.laborCostLow?.toLocaleString()} - ${item.laborCostHigh?.toLocaleString()}</span>
                                <span>•</span>
                                <span>Materials: ${item.materialCostLow?.toLocaleString()} - ${item.materialCostHigh?.toLocaleString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {quickEstimate.notes && (
                      <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-xs text-gray-700">{quickEstimate.notes}</p>
                      </div>
                    )}

                    {/* Next Steps */}
                    <div className="space-y-3">
                      <p className="text-xs md:text-sm text-gray-600">
                        This is a preliminary estimate based on your initial questionnaire. For a more detailed breakdown and refined estimate, continue with additional refinement questions.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                        <Button
                          onClick={handleContinue}
                          className="flex-1 text-sm md:text-base"
                          size="sm"
                        >
                          Continue to Detailed Estimate
                          <ChevronRight className="h-3 w-3 md:h-4 md:w-4 ml-2" />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowAllItems(true)}
                          className="flex-1 text-sm md:text-base"
                          size="sm"
                        >
                          View Work Items
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Project Estimate Display - Priority Display */}
          {projectEstimate && (
            <ProjectEstimate
              projectEstimate={projectEstimate}
              onViewDetails={handleViewEstimateDetails}
              onDownloadEstimate={handleDownloadEstimate}
            />
          )}

          {/* Work Items section temporarily removed - needs redesign */}
      </div>

      {/* Image Gallery Modal - Outside space-y container to avoid margin issues */}
      {imageGalleryOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Project Photos</h3>
              <button
                onClick={() => setImageGalleryOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <ImageGallery
                sowId={sowId!}
                className="w-full"
                isPublicMode={isPublicMode}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};